import React from 'react';
import {withSystem, Plugins, Component, Atom} from '@packages/systems/wfdl';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {
  EvaluatedNodeData,
  getChildren,
  getString,
  getInPojo,
} from '@packages/utilities/data-utils';
import {
  TListOfElement,
  TRecord,
  TEnum,
  ELiteral,
  EElement,
  EText,
  ERecord,
  EBoolean,
  ENumber,
  EEnum,
  EList,
} from '@packages/systems/wfdl-ast';

import {
  ERROR_STATE,
  LOGIN_UI_ERROR_CODES,
  RESET_PASSWORD_UI_ERROR_CODES,
  resetPasswordErrorStates,
} from '@packages/systems/users/constants';
import {Map} from 'immutable';

const UserResetPasswordFormWrapper = Atom.createRender(
  ({attributes, classes, generateAttributes, data, renderChild}) => {
    const formState = getString(getInPojo(data, ['temp', 'state'])) ?? 'normal';

    const context = Map({formState});

    return (
      <div {...generateAttributes(attributes, classes)}>
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, context)
        )}
      </div>
    );
  }
);

const config = createIntegratedAtom({
  constraints: {
    descendants: [
      {
        is: 'UserResetPasswordForm',
        rule: 'ExactlyOne',
      },
      {
        is: 'UserFormErrorState',
        rule: 'ExactlyOne',
      },
      {
        is: 'UserFormSuccessState',
        rule: 'ExactlyOne',
      },
    ],
    page: [
      {
        type: 'UtilityPage',
        is: 'usys-reset-password',
      },
    ],
  },
  displayName: 'Reset Password Form Block',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Form'), iconName('FormBlock')),
  },
  render: UserResetPasswordFormWrapper,
  showEmpty: true,
  fields: {
    temp: TRecord({
      state: withSystem(
        {label: 'State', defaultValue: 'normal', isFullWidth: true},
        TEnum({
          normal: 'Normal',
          success: 'Success',
          error: 'Error',
        })
      ),
    }),
    children: TListOfElement,
  },
  style: {
    base: {
      main: {
        noPseudo: {
          position: 'relative',
          marginBottom: px(0),
          paddingLeft: px(20),
          paddingRight: px(20),
        },
      },
    },
  },
});

export default config;

export const UserResetPasswordFormPreset = EElement({
  id: 'User Reset Password Form Wrapper',
  type: qualify(
    Plugins.name('Users'),
    Component.name('UserResetPasswordFormWrapper')
  ),
  data: ERecord({
    temp: ERecord({
      state: EEnum('normal'),
    }),
    children: EList([
      EElement({
        id: 'User Reset Password Success Message',
        type: qualify(
          Plugins.name('Users'),
          Component.name('UserFormSuccessState')
        ),
        data: ERecord({
          children: EList([
            EElement({
              id: 'User Form Header',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormHeader')
              ),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'Image',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Image')
                    ),
                    data: ERecord({
                      img: ELiteral({
                        name: qualify(Plugins.name('Asset'), typeName('Image')),
                        value: {},
                      }),
                      attr: ERecord({
                        src: EText(
                          'https://d3e54v103j8qbb.cloudfront.net/img/email-72.67fa6be437.svg'
                        ),
                        loading: EEnum('lazy'),
                        width: EText('auto'),
                        height: EText('auto'),
                        alt: EText('__wf_reserved_decorative'),
                      }),
                      temp: ERecord({
                        img: ERecord({
                          src: EText(''),
                        }),
                      }),
                      srcsetDisabled: EBoolean(false),
                      sizes: EList([]),
                    }),
                  }),
                  EElement({
                    id: 'Body Heading',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Heading')
                    ),
                    data: ERecord({
                      tag: EEnum('h2'),
                      children: EList([
                        EElement({
                          id: 'Body String',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Password Reset Email Sent'),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'Body Paragraph',
              type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'Body String 2',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText(
                      "If we found an account associated with that email address, we've sent a link to reset your password."
                    ),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
      EElement({
        id: 'User Reset Password Form',
        type: qualify(
          Plugins.name('Users'),
          Component.name('UserResetPasswordForm')
        ),
        data: ERecord({
          children: EList([
            EElement({
              id: 'User Form Header 2',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormHeader')
              ),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'Body Heading 2',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Heading')
                    ),
                    data: ERecord({
                      tag: EEnum('h2'),
                      children: EList([
                        EElement({
                          id: 'Body String 3',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Reset Password'),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Form Block Label',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormBlockLabel')
              ),
              data: ERecord({
                attr: ERecord({
                  for: EText('wf-reset-password-email'),
                }),
                users: ERecord({
                  field: EText(''),
                }),
                children: EList([
                  EElement({
                    id: 'Body String 5',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText('Email'),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Form Email Input',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormEmailInput')
              ),
              data: ERecord({
                attr: ERecord({
                  autofocus: EBoolean(false),
                  maxlength: ENumber(256),
                  placeholder: EText(''),
                  disabled: EBoolean(false),
                  name: EText('Email'),
                  id: EText('wf-reset-password-email'),
                }),
                xattr: EList([
                  ERecord({
                    name: EText('aria-describedby'),
                    value: EText('wf-reset-password-paragraph'),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Form Button',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormButton')
              ),
              data: ERecord({
                attr: ERecord({
                  type: EText('submit'),
                  value: EText('Reset Password'),
                  'data-wait': EText('Please wait...'),
                }),
              }),
            }),
          ]),
        }),
      }),
      EElement({
        id: 'User Reset Password Error State',
        type: qualify(
          Plugins.name('Users'),
          Component.name('UserFormErrorState')
        ),
        data: ERecord({
          users: ERecord({
            type: EText(ERROR_STATE.RESET_PASSWORD),
          }),
          children: EList([
            EElement({
              id: 'User Reset Password Error Msg',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserErrorMsg')
              ),
              data: ERecord({
                users: ERecord({
                  type: EText(ERROR_STATE.RESET_PASSWORD),
                }),
                temp: ERecord({
                  index: ENumber(0),
                }),
                errors: EList([
                  ERecord({
                    id: EText(
                      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                      resetPasswordErrorStates[
                        LOGIN_UI_ERROR_CODES.GENERAL_ERROR
                      ].id
                    ),
                    name: EText(
                      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                      resetPasswordErrorStates[
                        RESET_PASSWORD_UI_ERROR_CODES.GENERAL_ERROR
                      ].name
                    ),
                    copy: EText(
                      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                      resetPasswordErrorStates[
                        RESET_PASSWORD_UI_ERROR_CODES.GENERAL_ERROR
                      ].copy
                    ),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
    ]),
  }),
});
